import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCardIcon, PlusIcon, ArrowPathIcon, MinusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';

const RECHARGE_DESCRIPTION = "Recharge de crédit par un administrateur";
const RETRAIT_DESCRIPTION = "Retrait de crédit par un administrateur";

const TherapistCreditForm = ({ therapist }) => {
    const [creditsChange, setCreditsChange] = useState(0);
    const [currentCredits, setCurrentCredits] = useState(0);
    const [description, setDescription] = useState("");
    const queryClient = useQueryClient();

    useEffect(() => {
        if (therapist) {
            setCurrentCredits(therapist.currentCredits || 0);
        }
    }, [therapist]);

    useEffect(() => {
        // Mettre à jour la description par défaut lorsque le creditsChange change
        if (creditsChange > 0) {
            setDescription(RECHARGE_DESCRIPTION);
        } else if (creditsChange < 0) {
            setDescription(RETRAIT_DESCRIPTION);
        }
    }, [creditsChange]);

    const addCreditsMutation = useMutation({
        mutationFn: (data) => {
            return api.post(`${API.ADMIN_REFILL_CREDITS}`, data);
        },
        onSuccess: () => {
            // Mettre à jour l'UI avec le nouveau montant
            setCurrentCredits(prevCredits => prevCredits + Math.abs(creditsChange));
            // Réinitialiser les états
            setCreditsChange(0);
            setDescription("");
            // Rafraîchir les données
            queryClient.invalidateQueries({ queryKey: ['therapists'] });
        }
    });

    const removeCreditsMutation = useMutation({
        mutationFn: (data) => {
            return api.post(`${API.ADMIN_REMOVE_CREDITS}`, data);
        },
        onSuccess: () => {
            // Mettre à jour l'UI avec le nouveau montant
            setCurrentCredits(prevCredits => prevCredits - Math.abs(creditsChange));
            // Réinitialiser les états
            setCreditsChange(0);
            setDescription("");
            // Rafraîchir les données
            queryClient.invalidateQueries({ queryKey: ['therapists'] });
        }
    });

    const handleUpdate = () => {
        if (creditsChange === 0) return;

        const absoluteValue = Math.abs(creditsChange);
        const currentDescription = description.trim() || (creditsChange > 0 ? RECHARGE_DESCRIPTION : RETRAIT_DESCRIPTION);

        if (creditsChange > 0) {
            addCreditsMutation.mutate({
                userId: therapist.id,
                amount: absoluteValue,
                initiator: "Admin",
                description: currentDescription
            });
        } else {
            removeCreditsMutation.mutate({
                userId: therapist.id,
                amount: absoluteValue,
                initiator: "Admin",
                description: currentDescription
            });
        }
    };

    const adjustCredits = (amount) => {
        setCreditsChange(prev => prev + amount);
    };

    const isPending = addCreditsMutation.isPending || removeCreditsMutation.isPending;
    const newCreditBalance = currentCredits + creditsChange;

    // Classes dynamiques pour l'input en fonction de la valeur
    const getInputClass = () => {
        if (creditsChange > 0) {
            return "border-primary/30 bg-primary/5 text-primary";
        } else if (creditsChange < 0) {
            return "border-warning/30 bg-warning/5 text-warning";
        }
        return "border-gray-200 bg-transparent text-gray-600";
    };

    return (
        <div className="card bg-white border border-gray-100 shadow-sm rounded-lg">
            <div className="card-body p-5">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                    <CreditCardIcon className="h-5 w-5 text-primary" />
                    Gestion des crédits
                </h3>

                {/* Visualisation des crédits et contrôles de modification */}
                <div className="bg-gray-50 p-4 rounded-lg mb-5 border border-gray-100">
                    <h4 className="text-xs text-gray-500 mb-3">État des crédits</h4>

                    <div className="flex justify-between items-center mb-4">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gray-800">
                                {currentCredits}
                            </div>
                            <span className="text-xs text-gray-500">Actuels</span>
                        </div>

                        <div className="flex flex-col items-center">
                            {/* Boutons +1 et +5 */}
                            <div className="flex gap-1 mb-1">
                                <button
                                    type="button"
                                    className="btn btn-xs btn-warning btn-outline outline-none flex items-center justify-center rounded-lg transition-colors"
                                    onClick={() => adjustCredits(-1)}
                                    disabled={isPending}
                                >
                                    <span>-1</span>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-primary btn-outline outline-none flex items-center justify-center rounded-lg transition-colors"
                                    onClick={() => adjustCredits(+1)}
                                    disabled={isPending}
                                >
                                    <span>+1</span>
                                </button>
                            </div>

                            {/* Input stylisé */}
                            <div className="relative">
                                <input
                                    type="number"
                                    className={`w-16 h-10 text-center text-base font-medium rounded-lg shadow-sm input input-sm focus:ring-1 focus:ring-opacity-50 ${getInputClass()}`}
                                    value={creditsChange}
                                    onChange={(e) => setCreditsChange(parseInt(e.target.value) || 0)}
                                    aria-label="Modification de crédits"
                                />

                                <span className={`${creditsChange === 0 ? 'opacity-0' : 'opacity-100'} absolute -right-2 -top-2 flex items-center justify-center w-5 h-5 text-xs text-white font-bold rounded-full ${creditsChange > 0 ? 'bg-primary' : 'bg-warning'}`}>
                                    {creditsChange > 0 ? '+' : '-'}
                                </span>

                            </div>

                            {/* Boutons -1 et -5 */}
                            <div className="flex gap-1 mt-1">
                                <button
                                    type="button"
                                    className="btn btn-xs btn-warning btn-outline outline-none flex items-center justify-center rounded-lg transition-colors"
                                    onClick={() => adjustCredits(-5)}
                                    disabled={isPending}
                                >
                                    <span>-5</span>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-primary btn-outline outline-none flex items-center justify-center rounded-lg transition-colors"
                                    onClick={() => adjustCredits(+5)}
                                    disabled={isPending}
                                >
                                    <span>+5</span>
                                </button>
                            </div>
                        </div>

                        <div className="text-center">
                            <div className={`text-3xl font-bold ${creditsChange > 0 ? 'text-primary' : creditsChange < 0 ? 'text-gray-800' : 'text-gray-800'}`}>
                                {newCreditBalance}
                            </div>
                            <span className="text-xs text-gray-500">Après</span>
                        </div>
                    </div>
                </div>

                {/* Description et bouton de mise à jour */}
                <div className="flex flex-col gap-4">
                    {creditsChange !== 0 && (
                        <div>
                            <label htmlFor="description" className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <DocumentTextIcon className="h-4 w-4 mr-1 text-gray-500" />
                                Description
                            </label>
                            <div className="relative">
                                <input
                                    id="description"
                                    type="text"
                                    className={`w-full h-10 px-3 text-sm rounded-lg border focus:ring-1 focus:ring-opacity-50 ${creditsChange > 0 ? 'border-primary/30 focus:ring-primary focus:border-primary' : 'border-warning/30 focus:ring-warning focus:border-warning'}`}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={creditsChange > 0 ? "Recharge de crédit" : creditsChange < 0 ? "Retrait de crédit" : "Description"}
                                    disabled={isPending}
                                />
                            </div>
                        </div>
                    )}

                    <button
                        type="button"
                        className={`w-full flex items-center justify-center py-2 px-4 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${creditsChange > 0 ? 'bg-primary hover:bg-primary/90' : creditsChange < 0 ? 'bg-warning hover:bg-warning/90' : 'bg-primary hover:bg-primary/90'}`}
                        disabled={isPending || creditsChange === 0}
                        onClick={handleUpdate}
                    >
                        {isPending ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                {creditsChange > 0 ? (
                                    <>
                                        <PlusIcon className="h-4 w-4 mr-1" />
                                        Ajouter
                                    </>
                                ) : creditsChange < 0 ? (
                                    <>
                                        <MinusIcon className="h-4 w-4 mr-1" />
                                        Retirer
                                    </>
                                ) : 'Ajouter/Retirer'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TherapistCreditForm; 